import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AppLayout } from '../layouts/AppLayout';
import { api } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Search, 
  PlusCircle, 
  Loader2,
  ChevronLeft,
  ChevronRight,
  FileText
} from 'lucide-react';

export default function BidsListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [bids, setBids] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
  const limit = 20;

  useEffect(() => {
    fetchBids();
  }, [page, status]);

  const fetchBids = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (status) params.append('status', status);
      params.append('skip', ((page - 1) * limit).toString());
      params.append('limit', limit.toString());
      
      const res = await api.get(`/bids?${params.toString()}`);
      setBids(res.data.bids);
      setTotal(res.data.total);
    } catch (error) {
      console.error('Failed to fetch bids', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchBids();
  };

  const totalPages = Math.ceil(total / limit);

  const getMarginClass = (margin) => {
    if (margin >= 15) return 'text-green-600';
    if (margin >= 0) return 'text-orange-500';
    return 'text-red-600';
  };

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="bids-list-container">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Bids</h1>
            <p className="text-slate-500">{total} bids in your portfolio</p>
          </div>
          <Link to="/app/bids/new">
            <Button className="btn-primary" data-testid="bids-new-btn">
              <PlusCircle className="mr-2 w-5 h-5" />
              New Bid
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <Card className="card-industrial">
          <CardContent className="py-4">
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by ref or client..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="bids-search-input"
                />
              </div>
              <Select value={status || "all"} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setPage(1); }}>
                <SelectTrigger className="w-40" data-testid="bids-status-filter">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="won">Won</SelectItem>
                  <SelectItem value="lost">Lost</SelectItem>
                </SelectContent>
              </Select>
              <Button type="submit" variant="outline">
                Search
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Bids Table */}
        <Card className="card-industrial">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
              </div>
            ) : bids.length === 0 ? (
              <div className="text-center py-16">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No bids found</p>
                <Link to="/app/upload" className="text-orange-600 hover:underline text-sm mt-2 inline-block">
                  Upload your historical bids
                </Link>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Client</th>
                      <th>Project Type</th>
                      <th>Submitted Price</th>
                      <th>Margin</th>
                      <th>Status</th>
                      <th>Outcome</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bids.map((bid) => (
                      <tr key={bid.id}>
                        <td>
                          <Link 
                            to={`/app/bids/${bid.id}`} 
                            className="font-medium text-orange-600 hover:underline"
                            data-testid={`bid-link-${bid.id}`}
                          >
                            {bid.bid_ref}
                          </Link>
                        </td>
                        <td className="text-slate-600">{bid.client_name}</td>
                        <td className="text-slate-500">{bid.project_type || '-'}</td>
                        <td className="font-mono">£{bid.submitted_price?.toLocaleString()}</td>
                        <td>
                          <span className={`font-mono font-semibold ${getMarginClass(bid.gross_margin_pct)}`}>
                            {bid.gross_margin_pct?.toFixed(1)}%
                          </span>
                        </td>
                        <td>
                          <Badge variant="outline" className="capitalize">
                            {bid.status}
                          </Badge>
                        </td>
                        <td>
                          {bid.outcome ? (
                            <Badge variant={bid.outcome === 'won' ? 'default' : 'secondary'}>
                              {bid.outcome}
                            </Badge>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="flex items-center px-3 text-sm">
                {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
